import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setFabrics,
  setTotal,
  setPage,
  setSize,
  setSearch,
  setFilterType,
  setFabricCategories,
  setMetrics,
  setShowForm,
  setFabricDetails,
  loadEditingFabric,
  openFabricDetails,
  resetForm,
} from '../../../store/dataFabricSlice';
import { TopNav, SideNav } from '../navigation/ConsoleNavigation';
import axios from 'axios';
import DataFabricList from './DataFabricList';
import DataFabricForm from './DataFabricForm';
import DataFabricDetails from './DataFabricDetails';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import { useTimezone } from '../../utils/TimezoneContext';

const DataFabricManagement = () => {
  const dispatch = useDispatch();
  const { timezone } = useTimezone();

  // ╔══ Redux-persisted state (survives page navigation) ══╗
  const {
    fabrics,
    total,
    page,
    size,
    search,
    filterType,
    fabricCategories,
    metrics,
    showForm,
    editingFabric,
    fabricDetails,
  } = useSelector((state) => state.dataFabric);

  // Local state for category/tag filter (passed to list + fetch)
  const [categoryFilter, setCategoryFilter] = useState([]);

  // ╔══ Local transient state (resets on mount — intentional) ══╗
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/fabrics';
  const headers = { Authorization: GetCookie('access_token') };

  // Fetch fabrics on mount and when dependencies change
  useEffect(() => {
    fetchFabrics();
  }, [page, size, search, refreshTrigger, filterType, categoryFilter]);

  // Fetch metrics on mount
  useEffect(() => {
    fetchMetrics();
  }, []);

  useEffect(() => {
    if (fabricDetails?.fabric?.id) {
      fetchFabricDetails(fabricDetails.fabric.id);
    }
  }, []);

  // Fetch fabrics with pagination and search
  const fetchFabrics = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading projects');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/list`,
        { page, size, search: search || undefined, filter_type: filterType, category: categoryFilter.length > 0 ? categoryFilter : undefined },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load projects');
        setIsErrorPopupVisible(true);
        return;
      }

      dispatch(setFabrics(data.data || []));
      dispatch(setTotal(data.total || 0));
      dispatch(setFabricCategories(data.categories || ['General']));
    } catch (error) {
      console.error('Error fetching projects:', error);
      setPopupErrorMessage(error.message || 'Failed to load projects');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Fetch metrics
  const fetchMetrics = async () => {
    try {
      setIsInitialLoading(true);
      const response = await axios.get(`${API_BASE_URL}/metrics`, { headers });

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to load metrics');
        setIsErrorPopupVisible(true);
        return;
      }

      dispatch(setMetrics(data.data || {
        user_email: '',
        user_name: '',
        fabric_count: 0,
        thread_count: 0,
        team_count: 0,
      }));
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setPopupErrorMessage(error.message || 'Failed to load metrics');
      setIsErrorPopupVisible(true);
    } finally {
      setIsInitialLoading(false);
    }
  };

  // Fetch fabric details for viewing
  const fetchFabricDetails = async (fabricId) => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading projects details');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/details/${fabricId}`,
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load projects details');
        setIsErrorPopupVisible(true);
        return;
      }

      dispatch(setFabricDetails(data.data));
    } catch (error) {
      console.error('Error fetching projects details:', error);
      setPopupErrorMessage(error.message || 'Failed to load projects details');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Open form for editing a fabric (bulk action)
  const handleOpenEditForm = (fabric) => {
    dispatch(loadEditingFabric(fabric));
  };

  const handleOpenFabricDetails = (fabricId) => {
    dispatch(openFabricDetails());
    fetchFabricDetails(fabricId);
  };

  const handleCardClick = (fabricId) => {
    const fabric = fabrics.find((f) => f.id === fabricId);

    if (fabric?.category?.includes('SLR')) {
      navigate('/screening-space', { state: { openSlrFabricId: fabricId } });
      return;
    }

    handleOpenFabricDetails(fabricId);
  };

  // Close fabric details view
  const handleCloseFabricDetails = () => {
    dispatch(setFabricDetails(null));
  };

  // Handle form submission success
  const handleFormSuccess = (message) => {
    setPopupSuccessMessage(message);
    setIsSuccessPopupVisible(true);
    dispatch(resetForm());
    setRefreshTrigger(prev => prev + 1);
  };

  // Wrapper setters for child props
  const handleSetPage = (val) => dispatch(setPage(val));
  const handleSetSize = (val) => dispatch(setSize(val));
  const handleSetSearch = (val) => dispatch(setSearch(val));
  const handleSetFilterType = (val) => dispatch(setFilterType(val));

  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);

  // Greeting based on time of day
  const getGreeting = () => {
    let hour = new Date().getHours();

    try {
      const hourPart = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        hourCycle: 'h23',
      })
        .formatToParts(new Date())
        .find(part => part.type === 'hour');

      if (hourPart) {
        hour = Number(hourPart.value);
      }
    } catch {
      // Fall back to browser time if the selected timezone is unavailable.
    }

    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="main-window">
      <TopNav />
      <div className="container-fluid">
        <div className="row">
          <SideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="col-10 m-auto pt-5 pb-5">
              <div className="margin-top-2 margin-bottom-2">
                <p className="ev-small-p-blue ev-bold-reg ev-fs-14">
                  {getGreeting()},
                  <span
                    className="ev-bold-med avatar-tooltip ms-1"
                    data-tooltip={metrics.user_email}
                  >
                    {metrics.user_name}
                  </span>
                </p>
              </div>

              {/* Metrics Cards */}
              <div className="row mb-5">
                <div className="col-4 d-flex">
                  <div className="ev-card-5 ev-radius-small w-100">
                    <h5 className="ev-small-p-blue">Projects</h5>
                    <h2 className="ev-large-number-dark-blue mb-0">
                      {metrics.fabric_count < 10 ? `0${metrics.fabric_count}` : metrics.fabric_count}
                    </h2>
                    <p className="ev-message-card2 border-0 mb-0">
                      <i className="bi bi-tag"></i> Projects you have access to in your organization
                    </p>
                  </div>
                </div>
                <div className="col-4 d-flex">
                  <div className="ev-card-5 ev-radius-small w-100">
                    <h5 className="ev-small-p-blue">Data Collections</h5>
                    <h2 className="ev-large-number-dark-blue mb-0">
                      {metrics.thread_count < 10 ? `0${metrics.thread_count}` : metrics.thread_count}
                    </h2>
                    <p className="ev-message-card2 border-0 mb-0">
                      <i className="bi bi-tag"></i> Data Collections you have access to in your organization
                    </p>
                  </div>
                </div>
                <div className="col-4 d-flex">
                  <div className="ev-card-5 ev-radius-small w-100">
                    <h5 className="ev-small-p-blue">Your Teams</h5>
                    <h2 className="ev-large-number-dark-blue mb-0">
                      {metrics.team_count < 10 ? `0${metrics.team_count}` : metrics.team_count}
                    </h2>
                    <p className="ev-message-card2 border-0 mb-0">
                      <i className="bi bi-tag"></i> Teams you are part of across your organization
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Fabrics Section */}
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 mt-4">
                <p className="ev-med-p-blue mb-1">
                  <i className="bi bi-cart3"></i> Marketplace{' '}
                  <span className="ev-light-reg">({total})</span>
                </p>
                {!showForm && !fabricDetails && (
                  <button
                    className="ev-btn-yellow-fill-round ev-bold-reg"
                    onClick={() => {
                      dispatch(resetForm());
                      dispatch(setShowForm(true));
                    }}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Create Project
                  </button>
                )}
              </div>
              <p className="ev-message-card2 mb-4">
                Orchestrate your data ecosystem, transforming Data Collections into powerful decision Projects.
              </p>

              {/* Show form, fabric details, or fabric list based on state */}
              {showForm ? (
                <DataFabricForm
                  editingFabric={editingFabric}
                  onSuccess={handleFormSuccess}
                  onCancel={() => dispatch(resetForm())}
                  f_cats={fabricCategories}
                />
              ) : fabricDetails ? (
                <DataFabricDetails
                  fabricDetails={fabricDetails}
                  onClose={handleCloseFabricDetails}
                  onRefresh={() => fetchFabricDetails(fabricDetails.fabric.id)}
                />
              ) : (
                <DataFabricList
                  fabrics={fabrics}
                  total={total}
                  page={page}
                  setPage={handleSetPage}
                  size={size}
                  setSize={handleSetSize}
                  search={search}
                  setSearch={handleSetSearch}
                  filterType={filterType}
                  fabricCategories={fabricCategories}
                  setFilterType={handleSetFilterType}
                  categoryFilter={categoryFilter}
                  setCategoryFilter={(val) => { setCategoryFilter(val); dispatch(setPage(1)); }}
                  onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                  onEditFabric={handleOpenEditForm}
                  onViewFabricDetails={handleOpenFabricDetails}
                  onCardClick={handleCardClick}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
      {isErrorPopupVisible && <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />}
      {isSuccessPopupVisible && <SuccessPopup onClose={closeSuccessPopup} message={popupSuccessMessage} />}
    </div>
  );
};

export default DataFabricManagement;